import React, { memo, forwardRef, useState, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { TagType } from '~constants/tagType';
import Modal from 'common/components/modal';
import CounterOffer from '~components/counter-offer';
import { CREATE_COUNTER_OFFER } from '../queries';
import {
  handleConfirmedCounterOfferTrack,
  handleCancelNewCounterOfferTrack,
} from '../counterOfferEvent';
import useStyles from './styles';
import { message } from 'antd';

const SecondConfirmModal = memo(
  forwardRef(({ record, counterOffer, handleCloseModal, refetchBookingList }, ref) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const [visible, setVisible] = useState(false);
    const [needReselect, setNeedReselect] = useState(false);

    const [createCounterOffer, { loading: createLoading }] = useMutation(CREATE_COUNTER_OFFER, {
      fetchPolicy: 'no-cache',
      onCompleted: val => {
        if (val.createOrderCounterOffer?.counterOffer) {
          handleCloseModal();

          refetchBookingList();
        }
      },
      onError: e => {
        if (
          e?.graphQLErrors?.[0]?.message?.error === 'LISTING_STATE_UNEQUAL_TO_AVAILABLE_WITH_PRICE'
        ) {
          message.error(t('landlord.long_tail.counter_offer.listing.sold_out.error'));
        }
      },
    });

    const handleCancelConfirm = () => {
      handleCancelNewCounterOfferTrack(
        {
          ...record,
          ...counterOffer,
        },
        record,
        t,
      );
      setVisible(false);
    };

    const handleConfirmCreate = () => {
      const { totalPrice, propertyId, propertyName, unitTypeName, unitTypeCategory, ...params } =
        counterOffer;
      handleConfirmedCounterOfferTrack(
        {
          ...record,
          ...counterOffer,
        },
        record,
        t,
      );

      createCounterOffer({
        variables: {
          input: params,
        },
      });
    };

    useImperativeHandle(ref, () => ({
      handleOpenModal: boolean => {
        setVisible(true);
        setNeedReselect(boolean);
      },
    }));

    return (
      <Modal
        title={t('landlord.long_tail.booking.counter_confirm_modal.title')}
        width={812}
        visible={visible}
        wrapClassName={classes.confirmWrapper}
        destroyOnClose={true}
        okText={t('landlord.long_tail.booking.counter_modal.footer.confirm')}
        cancelButtonProps={{
          className: classes.cancelButton,
        }}
        okButtonProps={{
          className: classes.okButton,
          loading: createLoading,
        }}
        onOk={handleConfirmCreate}
        onCancel={handleCancelConfirm}
      >
        <h5 className={classes.subTitle}>
          {t('landlord.long_tail.booking.counter_confirm_modal.subTitle')}
        </h5>

        <CounterOffer
          t={t}
          property={{
            ...record,
            ...counterOffer,
          }}
          showTotalPrice
          showReselectNotice={needReselect}
          tagType={TagType.counterOffer}
          showTagDesc={false}
        />

        <CounterOffer
          t={t}
          property={record}
          showTotalPrice
          tagType={TagType.origin}
          showTagDesc={false}
        />
      </Modal>
    );
  }),
);

export default SecondConfirmModal;
